import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Highlight from '@tiptap/extension-highlight';
import { Button, Space, Divider, Tooltip, Dropdown } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  MenuOutlined,
  LinkOutlined,
  TableOutlined,
  HighlightOutlined,
  UndoOutlined,
  RedoOutlined,
  CodeOutlined,
  FontSizeOutlined,
} from '@ant-design/icons';
import './RichTextEditor.css';

const RichTextEditor = ({ value = '', onChange, placeholder, disabled = false }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Highlight.configure({ multicolor: true }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  // Update editor content when value prop changes (for form resets, etc.)
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  // Update editable state when disabled prop changes
  React.useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const headingItems = [
    { key: 'p', label: 'Paragraph', onClick: () => editor.chain().focus().setParagraph().run() },
    { key: 'h1', label: 'Heading 1', onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
    { key: 'h2', label: 'Heading 2', onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { key: 'h3', label: 'Heading 3', onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
    { key: 'h4', label: 'Heading 4', onClick: () => editor.chain().focus().toggleHeading({ level: 4 }).run() },
    { key: 'h5', label: 'Heading 5', onClick: () => editor.chain().focus().toggleHeading({ level: 5 }).run() },
    { key: 'h6', label: 'Heading 6', onClick: () => editor.chain().focus().toggleHeading({ level: 6 }).run() },
  ];

  const MenuButton = ({ icon, onClick, isActive, tooltip }) => (
    <Tooltip title={tooltip}>
      <Button
        size="small"
        type={isActive ? 'primary' : 'default'}
        icon={icon}
        onMouseDown={(e) => {
          e.preventDefault(); // Prevent focus loss
          onClick();
        }}
        disabled={disabled}
        style={{
          minWidth: 32,
          padding: '4px 8px',
        }}
      />
    </Tooltip>
  );

  return (
    <div className="rich-text-editor" style={{ border: '1px solid #d9d9d9', borderRadius: '6px', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div
        style={{
          background: '#fafafa',
          padding: '8px',
          borderBottom: '1px solid #d9d9d9',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
        }}
      >
        <Space size={4} wrap>
          {/* Headings */}
          <Dropdown menu={{ items: headingItems }} trigger={['click']}>
            <Button size="small" icon={<FontSizeOutlined />} disabled={disabled}>
              Style
            </Button>
          </Dropdown>

          <Divider type="vertical" />

          {/* Text Formatting */}
          <MenuButton
            icon={<BoldOutlined />}
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            tooltip="Bold (Ctrl+B)"
          />
          <MenuButton
            icon={<ItalicOutlined />}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            tooltip="Italic (Ctrl+I)"
          />
          <MenuButton
            icon={<UnderlineOutlined />}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            tooltip="Underline (Ctrl+U)"
          />
          <MenuButton
            icon={<StrikethroughOutlined />}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
            tooltip="Strikethrough"
          />
          <MenuButton
            icon={<CodeOutlined />}
            onClick={() => editor.chain().focus().toggleCode().run()}
            isActive={editor.isActive('code')}
            tooltip="Inline Code"
          />
          <MenuButton
            icon={<HighlightOutlined />}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            isActive={editor.isActive('highlight')}
            tooltip="Highlight"
          />

          <Divider type="vertical" />

          {/* Lists */}
          <MenuButton
            icon={<UnorderedListOutlined />}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            tooltip="Bullet List"
          />
          <MenuButton
            icon={<OrderedListOutlined />}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            tooltip="Numbered List"
          />

          <Divider type="vertical" />

          {/* Alignment */}
          <MenuButton
            icon={<AlignLeftOutlined />}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
            tooltip="Align Left"
          />
          <MenuButton
            icon={<AlignCenterOutlined />}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
            tooltip="Align Center"
          />
          <MenuButton
            icon={<AlignRightOutlined />}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            isActive={editor.isActive({ textAlign: 'right' })}
            tooltip="Align Right"
          />
          <MenuButton
            icon={<MenuOutlined />}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            isActive={editor.isActive({ textAlign: 'justify' })}
            tooltip="Justify"
          />

          <Divider type="vertical" />

          {/* Link & Table */}
          <MenuButton
            icon={<LinkOutlined />}
            onClick={setLink}
            isActive={editor.isActive('link')}
            tooltip="Insert Link"
          />
          <MenuButton
            icon={<TableOutlined />}
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            tooltip="Insert Table"
          />

          <Divider type="vertical" />

          {/* Undo/Redo */}
          <MenuButton
            icon={<UndoOutlined />}
            onClick={() => editor.chain().focus().undo().run()}
            tooltip="Undo (Ctrl+Z)"
          />
          <MenuButton
            icon={<RedoOutlined />}
            onClick={() => editor.chain().focus().redo().run()}
            tooltip="Redo (Ctrl+Shift+Z)"
          />
        </Space>
      </div>

      {/* Editor Content */}
      <div
        style={{
          padding: '12px',
          minHeight: '200px',
          background: disabled ? '#f5f5f5' : '#fff',
        }}
      >
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
    </div>
  );
};

export default RichTextEditor;
